import { devLog } from '../../utils/devLog';
import React, { useState } from 'react';
import { useAppContext } from '../../hooks/useAppContext';
import { useToast } from '../common/Toast';
import { createRole } from '../../utils/api';

const DEFAULT_WEIGHTS = { wT: 25, wE: 25, wP: 20, wS: 15, wF: 15 };

const WEIGHT_LABELS = {
  wT: { label: 'Technical Skills', emoji: '⚙️', description: 'Tools, languages, frameworks' },
  wE: { label: 'Experience',       emoji: '💼', description: 'Work history relevance' },
  wP: { label: 'Projects',         emoji: '🚀', description: 'Real-world project depth' },
  wS: { label: 'Structure',        emoji: '📋', description: 'CV clarity and formatting' },
  wF: { label: 'Role Fit',         emoji: '🎯', description: 'Overall match to JD' },
};

const JobForm = () => {
  const { addJob } = useAppContext();
  const { showSuccess, showError } = useToast();
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    requirements: ''
  });
  const [weights, setWeights] = useState({ ...DEFAULT_WEIGHTS });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const totalWeight = Object.values(weights).reduce((a, b) => a + b, 0);
  const isWeightValid = totalWeight === 100;

  const handleChange = (e) => {
    const { name, value } = e.target;
    if (name === 'title') {
      if (!/^[A-Za-z\s]*$/.test(value)) return;
    }
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleWeightChange = (key, value) => {
    const val = Math.min(100, Math.max(0, parseInt(value) || 0));
    setWeights(prev => ({ ...prev, [key]: val }));
  };

  const handleAutoBalance = () => {
    // Distribute remaining weight proportionally among other keys
    setWeights({ wT: 20, wE: 20, wP: 20, wS: 20, wF: 20 });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!isWeightValid) {
      showError(`Weights must total 100%. Currently: ${totalWeight}%`, 5000);
      return;
    }
    setIsSubmitting(true);
    try {
      const result = await createRole({
        ...formData,
        scoring_weights: {
          wT: weights.wT / 100,
          wE: weights.wE / 100,
          wP: weights.wP / 100,
          wS: weights.wS / 100,
          wF: weights.wF / 100,
        }
      });

      if (result.success) {
        addJob(result.role);
        setFormData({ title: '', description: '', requirements: '' });
        setWeights({ ...DEFAULT_WEIGHTS });
        showSuccess(`Job position "${result.role.title}" created successfully!`, 5000);
      } else {
        showError(result.error || 'Failed to create job position', 6000);
      }
    } catch (err) {
      devLog.error('Error creating job:', err);
      showError('An unexpected error occurred. Please try again.', 6000);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <form onSubmit={handleSubmit}>
      <div className="form-group">
        <label className="form-label">Job Title *</label>
        <input
          type="text"
          className="form-input"
          name="title"
          value={formData.title}
          onChange={handleChange}
          placeholder="e.g., Senior Software Engineer"
          required
          disabled={isSubmitting}
        />
      </div>

      <div className="form-group">
        <label className="form-label">Job Description *</label>
        <textarea
          className="form-textarea"
          name="description"
          value={formData.description}
          onChange={handleChange}
          placeholder="Describe the role, responsibilities, and work environment..."
          required
          disabled={isSubmitting}
        />
      </div>

      <div className="form-group">
        <label className="form-label">Job Requirements *</label>
        <textarea
          className="form-textarea"
          name="requirements"
          value={formData.requirements}
          onChange={handleChange}
          placeholder="List required skills, qualifications, and experience..."
          required
          disabled={isSubmitting}
        />
      </div>

      {/* CV Scoring Weights */}
      <div className="form-group">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
          <label className="form-label" style={{ margin: 0 }}>
            CV Scoring Weights
          </label>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <span style={{
              fontSize: '0.85rem',
              fontWeight: 700,
              padding: '4px 12px',
              borderRadius: '20px',
              background: isWeightValid ? '#d1fae5' : '#fee2e2',
              color: isWeightValid ? '#065f46' : '#991b1b'
            }}>
              Total: {totalWeight}% {isWeightValid ? '✅' : '❌ must be 100%'}
            </span>
            <button
              type="button"
              onClick={handleAutoBalance}
              style={{
                fontSize: '0.8rem',
                padding: '4px 10px',
                borderRadius: '6px',
                border: '1px solid #667eea',
                background: 'white',
                color: '#667eea',
                cursor: 'pointer',
                fontWeight: 500
              }}
            >
              ⚖️ Reset Equal
            </button>
          </div>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: '10px' }}>
          {Object.entries(WEIGHT_LABELS).map(([key, meta]) => (
            <div key={key} style={{
              background: '#f8fafc',
              border: '1px solid #e2e8f0',
              borderRadius: '10px',
              padding: '12px',
              textAlign: 'center'
            }}>
              <div style={{ fontSize: '1.2rem', marginBottom: '4px' }}>{meta.emoji}</div>
              <div style={{ fontSize: '0.75rem', fontWeight: 700, color: '#2d3748', marginBottom: '2px' }}>
                {meta.label}
              </div>
              <div style={{ fontSize: '0.65rem', color: '#718096', marginBottom: '8px' }}>
                {meta.description}
              </div>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '4px' }}>
                <input
                  type="number"
                  min="0"
                  max="100"
                  step="5"
                  value={weights[key]}
                  onChange={(e) => handleWeightChange(key, e.target.value)}
                  disabled={isSubmitting}
                  style={{
                    width: '52px',
                    textAlign: 'center',
                    padding: '4px',
                    borderRadius: '6px',
                    border: '2px solid #667eea',
                    fontSize: '0.9rem',
                    fontWeight: 700,
                    color: '#667eea',
                    outline: 'none'
                  }}
                />
                <span style={{ fontSize: '0.8rem', color: '#718096' }}>%</span>
              </div>
            </div>
          ))}
        </div>
        <p style={{ fontSize: '0.78rem', color: '#718096', marginTop: '8px' }}>
          💡 Define how much each component contributes to the CV score. All weights must add up to 100%.
        </p>
      </div>

      <button
        type="submit"
        className="button button-primary"
        disabled={isSubmitting || !isWeightValid}
        style={{
          opacity: isSubmitting || !isWeightValid ? 0.6 : 1,
          cursor: isSubmitting || !isWeightValid ? 'not-allowed' : 'pointer'
        }}
      >
        {isSubmitting ? <>⏳ Creating...</> : <>💾 Save Job Position</>}
      </button>
    </form>
  );
};

export default JobForm;